import { useNavigate, useParams } from 'react-router-dom';
import { useTaskFiltering } from './useTaskFiltering';
import { useGetUsersWithWorkspaceAccess, useGetWorkspace } from '../serverState/workspace';
import { WorkspaceFramework } from '../types/Workspace';
import { WorkspaceView, WorkspaceViewGroup } from '../types/WorkspaceView';
import { useGetTasks } from '../serverState/tasks';
import { Column } from '../types/WorkspaceViewColumn';
import { WorkspaceViewSortDirection } from '../types/WorkspaceViewSortBy';
import { useEpics } from '../serverState/epics';
import { sortStatusOptions, sortGroupTasks } from '../components/TaskManagement/helpers/sortHelpers';
import { defaultTaskStatuses } from '../types/TaskStatus';
import { useTaskFilterManagement } from './useTaskFilterManagement';
import { buildTaskUrl } from '../utils/urlUtils';
import { useActiveOrganization } from '../common/auth';
import Task from '../types/Task';
import { isExpandColumnVisible } from '../components/TaskManagement/VirtualizedTaskList/columnVisibility';
import { scopeTasksForView } from '../utils/taskListUtils';

interface TaskNavigation {
  previousTaskId: number | null;
  nextTaskId: number | null;
}

export const useTaskNavigation = (view?: WorkspaceView, parentTaskId?: number): TaskNavigation => {
  const { taskId } = useParams<{ taskId: string }>();
  const workspaceQuery = useGetWorkspace();
  const spaceId = view?.workspaceId ?? workspaceQuery.data?.id ?? 0;
  const { data: tasks } = useGetTasks(spaceId);
  const epicsQuery = useEpics(spaceId);
  const usersWithAccessToSpaceQuery = useGetUsersWithWorkspaceAccess(spaceId);
  const framework = workspaceQuery.data?.framework ?? WorkspaceFramework.scrum;

  // Filters
  const { epicFilter, ownerFilter, statusFilter, deadlineFilter, priorityFilter, estimateFilter } =
    useTaskFilterManagement(view, usersWithAccessToSpaceQuery.data, () => {});

  // Walk the same rows the list view shows (Triage, Backlog, Sprint/Kanban). #3242
  // Archive and Epics are left as before: Archive reads a different query than this hook,
  // and Epics rows are not tasks.
  // Subitems are kept here; the root/sibling filter below decides which ones navigate.
  const isTriageView = view?.group === WorkspaceViewGroup.triage;
  const scopesByView = !!view && view.group !== WorkspaceViewGroup.archive && view.group !== WorkspaceViewGroup.epics;
  const filteredTasks =
    tasks && scopesByView
      ? scopeTasksForView(tasks, view.group, workspaceQuery.data?.activeSprintId, { keepSubitems: true })
      : tasks;

  const { unassignedTasks, tasksPerGroup } = useTaskFiltering({
    dataSource: filteredTasks ?? [],
    selectedFilters: view?.filters ?? [],
    searchValue: '',
    groupBy: (view?.groupBy as Column) ?? Column.epic,
    sortBy: view?.sortBy ?? WorkspaceViewSortDirection.desc,
    workspaceQuery: workspaceQuery,
    filters: view?.filters ?? [],
    epicFilter: epicFilter,
    ownerFilter: ownerFilter,
    statusFilter: statusFilter,
    deadlineFilter: deadlineFilter,
    priorityFilter: priorityFilter,
    estimateFilter: estimateFilter,
    epics: epicsQuery.data ?? [],
    usersWithAccessToSpace: usersWithAccessToSpaceQuery.data ?? [],
    framework,
    // Match the table: with subitems off, dimmed context parents are not navigable rows. #2324
    hideSubitems: !isExpandColumnVisible({
      hiddenColumns: view?.columns?.hidden ?? [],
      viewEntityType: view?.entityType,
      viewGroup: view?.group,
    }),
  });

  const taskOrder = workspaceQuery.data?.TaskOrder[0];
  const tasksOrderMap = new Map<number, number>(taskOrder?.taskIds?.map((id: number, index: number) => [id, index]));

  const sortByColumn = view?.sortBy?.column;
  const sortDirection = view?.sortBy?.direction;
  const groupBy = view?.groupBy;

  const statusOrder = sortStatusOptions(workspaceQuery.data?.TaskStatus || defaultTaskStatuses).map(
    (status) => status.id
  );
  const sortedTasksPerGroup =
    tasksPerGroup?.map((groupTasks) =>
      sortGroupTasks(
        groupTasks.tasks,
        tasksOrderMap,
        sortByColumn,
        sortDirection,
        undefined,
        undefined,
        workspaceQuery.data?.estimationType,
        statusOrder
      )
    ) ?? [];

  const mappedUnassignedTasks = sortGroupTasks(
    unassignedTasks,
    tasksOrderMap,
    sortByColumn,
    sortDirection,
    undefined,
    undefined,
    workspaceQuery.data?.estimationType,
    statusOrder
  ).filter((t) => {
    // #2309: borrowed/context copies share their home row's id — skip them so navigation
    // resolves a single, canonical position per task.
    if (t?.isGroupContext) return false;
    // Triage lists subitems flat alongside their parents, so it navigates as one list.
    if (isTriageView) return true;
    if (parentTaskId) {
      return t?.parentTaskId === parentTaskId;
    }
    return !t?.parentTaskId;
  });
  const mappedTasksPerGroup = sortedTasksPerGroup.flat().filter((t) => {
    if (t?.isGroupContext) return false;
    // Triage lists subitems flat alongside their parents, so it navigates as one list.
    if (isTriageView) return true;
    if (parentTaskId) {
      return t?.parentTaskId === parentTaskId;
    }
    return !t?.parentTaskId;
  });
  // Dedupe by id: a task can still appear in multiple groups (e.g. multi-label grouping),
  // and prev/next must treat each task as a single navigable entry.
  const seenNavIds = new Set<number>();
  const sortedTasks = [...mappedTasksPerGroup, ...mappedUnassignedTasks].filter((t) =>
    seenNavIds.has(t.id) ? false : (seenNavIds.add(t.id), true)
  );

  if (!taskId || !tasks) {
    return {
      previousTaskId: null,
      nextTaskId: null,
    };
  }

  // Find current task index in the filtered tasks
  const currentTaskIndex = sortedTasks.findIndex((t) => t.taskId === Number(taskId));

  // Get previous and next task IDs
  const previousTask = currentTaskIndex > 0 ? sortedTasks[currentTaskIndex - 1] : null;
  const nextTask = currentTaskIndex < sortedTasks.length - 1 ? sortedTasks[currentTaskIndex + 1] : null;

  return {
    previousTaskId: previousTask?.taskId ?? null,
    nextTaskId: nextTask?.taskId ?? null,
  };
};

// Hook for navigating to tasks with proper org/space context
export const useNavigateToTask = () => {
  const navigate = useNavigate();
  const { orgId, spaceId } = useParams<{ orgId: string; spaceId: string }>();
  const { data: activeOrganization } = useActiveOrganization();
  const { data: workspace } = useGetWorkspace();

  return (
    task: Task | { workspaceId: number | string; taskId: number | string },
    options?: { state?: unknown }
  ) => {
    // Get orgId from params or fallback to active organization
    const resolvedOrgId = orgId || activeOrganization?.id;

    if (!resolvedOrgId) {
      console.error('No organization context available for task navigation');
      return;
    }

    // A task always lives in its own workspace, so task.workspaceId is the source
    // of truth for routing. Prefer the current URL/active space identifier (keeps
    // the pretty publicId URL) only when the task actually belongs to that space;
    // otherwise route by the task's own workspaceId. Without this, opening a task
    // from another space — the cross-space All Tasks view (no spaceId in the URL,
    // stale active workspace) or a notification while sitting in a different
    // space — routed into the wrong, empty space and rendered a blank page.
    const taskInActiveSpace = task.workspaceId === workspace?.id || task.workspaceId === workspace?.publicId;

    const spaceIdToUse = (taskInActiveSpace ? spaceId || workspace?.publicId : task.workspaceId) || '';

    const taskUrl = buildTaskUrl({
      orgId: resolvedOrgId,
      spaceId: spaceIdToUse,
      taskId: task.taskId,
    });

    navigate(taskUrl, options);
  };
};
